import pdfToText from "react-pdftotext";

export const parseCbsReportFromPdf = async (fileUrl) => {
  if (!fileUrl) return null;
  const response = await fetch(fileUrl);
  if (!response.ok) {
    throw new Error("Unable to download CBS report.");
  }
  const blob = await response.blob();
  const file = new File([blob], "cbs-report.pdf", {
    type: blob.type || "application/pdf",
  });
  const creditFile = await pdfToText(file);
  if (!creditFile) return null;

  const extractTextBetween = (beforeText, afterText) => {
    const beforeTextPattern = beforeText.replace(/\s+/g, "\\s+");
    const afterTextPattern = afterText.replace(/\s+/g, "\\s+");
    const regex = new RegExp(`${beforeTextPattern}(.*?)${afterTextPattern}`);
    const match = creditFile.match(regex);
    if (match && match[1]) {
      return match[1].trim();
    }
    return null;
  };

  const extractTextBetweeforloandetails = (creditText, beforeText, afterText) => {
    const startIndex = creditText.indexOf(beforeText);
    if (startIndex === -1) {
      return null;
    }
    const adjustedStartIndex = startIndex + beforeText.length;
    const endIndex = creditText.indexOf(afterText, adjustedStartIndex);
    if (endIndex === -1) {
      return null;
    }
    return creditText.substring(adjustedStartIndex, endIndex);
  };

  const removeCharactersAroundPage = (inputString) => {
    const regex = /(.{0,19})page(.{0,7})/g;
    return inputString.replace(regex, (match, p1, p2) => {
      const charsToRemoveBefore = 19 - 11;
      const charsToRemoveAfter = 11 - 4;
      const removedStart =
        p1.length > charsToRemoveBefore ? p1.slice(0, -charsToRemoveBefore) : "";
      const removedEnd =
        p2.length > charsToRemoveAfter ? p2.slice(charsToRemoveAfter) : "";
      return `${removedStart}page${removedEnd}`;
    });
  };

  const splitStringToRows = (inputString) =>
    inputString.split(/(?<! ) {2}(?! )/);

  const parseData = (inputString) => {
    const regex =
      /([a-zA-Z]+\s\d{4})\s+All\s+All\s+[\d,.]+\s+([\d,.]+\.\d{2})/g;
    const result = [];
    let match;
    while ((match = regex.exec(inputString)) !== null) {
      result.push({ date: match[1], value: match[2] });
    }
    return result;
  };

  const getMaxValue = (data) =>
    data.reduce((max, current) => {
      const currentValue = parseFloat(current.value.replace(/,/g, ""));
      return currentValue > max ? currentValue : max;
    }, 0);

  const getNextMonth = (data) => {
    const monthNames = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];
    const firstDate = data?.[0]?.date;
    if (!firstDate) return null;
    const [firstMonth, firstYear] = firstDate.split(" ");
    const monthIndex = monthNames.indexOf(firstMonth.toLowerCase());
    const year = parseInt(firstYear, 10);
    if (Number.isNaN(monthIndex) || Number.isNaN(year)) return null;
    const nextMonthIndex = (monthIndex + 1) % 12;
    const nextYear = monthIndex === 11 ? year + 1 : year;
    const nextMonth = monthNames[nextMonthIndex];
    return `${nextMonth} ${nextYear}`;
  };

  const splitExactlyTwoSpaces = (inputString) => inputString.split(/(?<! )  (?! )/);
  const combineSingleOrJoint = (splitArray) => {
    const combinedArray = [];
    for (let i = 0; i < splitArray.length; i += 1) {
      if (
        splitArray[i].startsWith("single") ||
        splitArray[i].startsWith("joint")
      ) {
        if (combinedArray.length > 0) {
          combinedArray[combinedArray.length - 1] += `  ${splitArray[i]}`;
        } else {
          combinedArray.push(splitArray[i]);
        }
      } else {
        combinedArray.push(splitArray[i]);
      }
    }
    return combinedArray;
  };

  const combinePrivateResidentialPurchase = (splitArray) => {
    const combinedArray = [];
    for (let i = 0; i < splitArray.length; i += 1) {
      if (splitArray[i].includes("private residential purchase")) {
        combinedArray.push(`${splitArray[i]}  ${splitArray[i + 1] || ""}`.trim());
        i += 1;
      } else {
        combinedArray.push(splitArray[i]);
      }
    }
    return combinedArray;
  };

  const formatAndMergeString = (input) => {
    const patterns = [
      "unsecured overdraft",
      "unsecured credit card",
      "other",
      "motor vehicle loan",
      "hdb loan",
      "private residential purchase",
      "unsecured personal loan",
    ];
    const regex = new RegExp(`(${patterns.join("|")})`, "g");
    const formattedString = input
      .split(regex)
      .filter(Boolean)
      .map((str) => str.trim())
      .join("\n");
    const lines = formattedString.split("\n");
    const result = [];
    for (let i = 0; i < lines.length; i += 2) {
      if (lines[i + 1]) {
        const record = [lines[i]].concat(
          lines[i + 1].split(/\s{3,}/).map((part) => part.trim())
        );
        result.push(record);
      } else {
        result.push([lines[i]]);
      }
    }
    return result;
  };

  const hasOverdueBalance = (data) =>
    data.some((item) => {
      const fifthIndex = item[4];
      return !Number.isNaN(Number(fifthIndex)) && Number(fifthIndex) > 0;
    });

  const textWithoutPage = removeCharactersAroundPage(creditFile.toLowerCase()).replace(
    /page/g,
    ""
  );

  const extract = extractTextBetweeforloandetails(
    textWithoutPage,
    "aggregated outstanding balances  month   product type   grantor bank   secured balances  unsecured balances interest bearing  unsecured balances non-interest bearing  exempted unsecured balances  ",
    "total"
  );

  const extract2ForMonthlyCommitment = extractTextBetweeforloandetails(
    textWithoutPage,
    "aggregated monthly instalment",
    "aggregated monthly instalments for preceding 5 months"
  );

  const extract3ForChartingOutstandingBalance = extractTextBetweeforloandetails(
    creditFile,
    "Aggregated Outstanding Balances for Preceding 5 Months",
    "Aggregated Monthly Instalment"
  );

  const extract4ForChartingOutstandingBalance = extractTextBetweeforloandetails(
    textWithoutPage,
    "aggregated monthly instalment  month   product type   grantor bank   account type**   property***   non-property   unsecured  exempted unsecured  ",
    "aggregated monthly instalments for preceding 5 months"
  );

  const chartParsed = extract3ForChartingOutstandingBalance
    ? parseData(extract3ForChartingOutstandingBalance)
    : [];
  if (chartParsed.length > 0) {
    const totalValue = extractTextBetween(
      "Total  ",
      " Aggregated Outstanding Balances for Preceding 5 Months"
    )
      ?.split("   ")[1]
      ?.trim();
    chartParsed.unshift({
      date: getNextMonth(chartParsed),
      value: totalValue || chartParsed[0]?.value || "",
    });
    chartParsed.reverse();
  }
  const chartMaxValue = chartParsed.length ? getMaxValue(chartParsed) : null;

  const cbsSupplementary =
    extract4ForChartingOutstandingBalance && extract4ForChartingOutstandingBalance !== ""
      ? combineSingleOrJoint(
          combinePrivateResidentialPurchase(
            splitExactlyTwoSpaces(extract4ForChartingOutstandingBalance)
          )
        ).filter(
          (row) =>
            row.includes("private residential purchase") ||
            row.includes("motor vehicle loan") ||
            row.includes("hdb loan")
        )
      : null;

  const monthlyCommitment =
    extract2ForMonthlyCommitment && extract2ForMonthlyCommitment !== ""
      ? parseFloat(
          extract2ForMonthlyCommitment
            .trim()
            .split("   ")
            [extract2ForMonthlyCommitment.trim().split("   ").length - 4]
            ?.replace(/,/g, "")
        ) +
        parseFloat(
          extract2ForMonthlyCommitment
            .trim()
            .split("   ")
            [extract2ForMonthlyCommitment.trim().split("   ").length - 3]
            ?.replace(/,/g, "")
        )
      : null;

  const cbsLoans =
    extract && extract !== ""
      ? splitStringToRows(extract).filter(
          (row) =>
            row.includes("private residential purchase") ||
            row.includes("motor vehicle loan") ||
            row.includes("hdb loan")
        )
      : null;

  const defaults = parseFloat(
    extractTextBetween("Defaults   ", "  Bankruptcy Proceedings   ")
  );
  const bankcruptcyProceedings = parseFloat(
    extractTextBetween(
      "  Bankruptcy Proceedings   ",
      "  Secured Credit Limit "
    )
  );

  const targetLine =
    "  account status history product type   grantor bank   account type date opened / date closed   overdue balance last 12 cycles / cash advance / balance transfer full payment  ";
  const extract8ForOverdueBalance = extractTextBetweeforloandetails(
    textWithoutPage,
    "account status history",
    "previous enquiries  date "
  )
    ?.replace(new RegExp(targetLine, "g"), " ")
    ?.replace(/account status history/g, "")
    ?.replace(/product type/g, "")
    ?.replace(/grantor bank/g, "")
    ?.replace(/account type/g, "")
    ?.replace(/date opened \/ date closed/g, "")
    ?.replace(/overdue balance/g, "")
    ?.replace(
      /last 12 cycles \/ cash advance \/ balance transfer full payment/g,
      ""
    )
    ?.trim();
  const overdueBalance = extract8ForOverdueBalance
    ? hasOverdueBalance(formatAndMergeString(extract8ForOverdueBalance))
    : null;

  return {
    EnquiryDate: extractTextBetween("enquiry date", "reference"),
    SecuredCreditLimit: parseFloat(
      extractTextBetween("Secured Credit Limit", "Unsecured Credit Limit")?.replace(
        /,/g,
        ""
      )
    ),
    UnsecuredCreditLimit: parseFloat(
      extractTextBetween("Unsecured Credit Limit", "Exempted Credit Limit")?.replace(
        /,/g,
        ""
      )
    ),
    CreditScore: parseFloat(
      extractTextBetween("financial institution.  Score", "Risk Grade")
    ),
    RiskGrade: extractTextBetween("Risk Grade ", "Risk Grade Description"),
    ProbabilityofDefault: parseFloat(
      extractTextBetween(
        "Probability of Default ",
        " For more details on the description"
      )?.replace(/,/g, "")
    ),
    SecuredCreditUtilized: extractTextBetween(
      "Total  ",
      " Aggregated Outstanding Balances for Preceding 5 Months"
    )
      ?.split("  ")
      ?.map((num) => parseFloat(num.replace(/,/g, "")))[0],
    UnsecuredCreditUtilized: extractTextBetween(
      "Total  ",
      " Aggregated Outstanding Balances for Preceding 5 Months"
    )
      ?.split("  ")
      ?.map((num) => parseFloat(num.replace(/,/g, "")))[1],
    SecuredCreditUtilizationinpercentage: (
      (extractTextBetween(
        "Total  ",
        " Aggregated Outstanding Balances for Preceding 5 Months"
      )
        ?.split("  ")
        ?.map((num) => parseFloat(num.replace(/,/g, "")))[0] /
        parseFloat(
          extractTextBetween("Secured Credit Limit", "Unsecured Credit Limit")?.replace(
            /,/g,
            ""
          )
        )) *
      100
    )?.toFixed(2),
    UnsecuredCreditUtilizationinpercentage: (
      (extractTextBetween(
        "Total  ",
        " Aggregated Outstanding Balances for Preceding 5 Months"
      )
        ?.split("  ")
        ?.map((num) => parseFloat(num.replace(/,/g, "")))[1] /
        parseFloat(
          extractTextBetween("Unsecured Credit Limit", "Exempted Credit Limit")?.replace(
            /,/g,
            ""
          )
        )) *
      100
    )?.toFixed(2),
    MonthlyCommitment: monthlyCommitment
      ? monthlyCommitment.toLocaleString("en-US", {
          style: "currency",
          currency: "SGD",
        })
      : null,
    NoteworthyOngoingLoans: cbsLoans && cbsLoans.length ? cbsLoans : null,
    Last6MonthsGraphChartMaxData: chartMaxValue && chartParsed.length
      ? chartMaxValue
      : null,
    Last6MonthsGraphChartData: chartMaxValue && chartParsed.length
      ? chartParsed
      : null,
    CBSImpLoanSupplementaryInstallment: cbsSupplementary,
    defaults: Number.isNaN(defaults) ? null : defaults,
    bankcruptcyProceedings: Number.isNaN(bankcruptcyProceedings)
      ? null
      : bankcruptcyProceedings,
    overdueBalance: overdueBalance,
  };
};
